/**
 * The Reports tab's sub-menu, and which permissions each view behind it needs.
 *
 * WHY THIS FILE EXISTS
 *
 * Reports was a single view — the PDF generator — so the tab needed no sub-menu
 * and had none. Monthly Expenditures is the second, and it is not open to
 * everyone the first one is: it is nothing but cost figures, so it needs
 * inventory.costs as well as reports.view, and its endpoint enforces both.
 *
 * The list lives here rather than in SubNav.jsx for the reason salesSubTabs.js
 * gives: CarsNav owns the sub-menu state and has to know where Reports may land,
 * so two files would otherwise each keep their own idea of a view's permission.
 * Dependency-free on purpose, like salesSubTabs.js and tabPermissions.js —
 * SubNav pulls in every sheet component in the app, and a test of these
 * functions must not mount half of it.
 *
 * `requiresAll` IS NOT `perms`
 *
 * salesSubTabs.js uses `perms`, which is any-of, matching `can(...perms)`. That
 * is the right shape there and the wrong one here: `can('reports.view',
 * 'inventory.costs')` passes for anyone holding EITHER, which would show the
 * expenditures view to a Sales Manager and then have the server refuse it — the
 * "tab whose only possible outcome is an error" that salesSubTabs.js was written
 * to remove. The name is different from `perms` deliberately, so the two cannot
 * be confused at a glance or copied between files without noticing.
 *
 * No `requiresAll` at all means everyone, and declaration order is strip order.
 */
/**
 * The permissions the expenditures surfaces demand, in one place.
 *
 * The pairing had been written out by hand in four components and the sub-tab
 * list, and the node route spells it a fifth way (two chained requirePermission
 * calls — the repos cannot share). Five copies of an access rule is four chances
 * for a surface to render something the server then refuses, which is the exact
 * failure salesSubTabs.js was extracted to stop.
 */
export const EXPENDITURES_PERMISSIONS = Object.freeze(["reports.view", "inventory.costs"]);

/**
 * Whether this user may see the expenditures page, dashboard card and PDF section.
 *
 * ALL of them, not any. `can` is variadic any-of, so `can(...EXPENDITURES_PERMISSIONS)`
 * would be true for anyone holding either — hence `.every` over single-permission
 * calls.
 */
export const canSeeExpenditures = (can) => EXPENDITURES_PERMISSIONS.every((p) => can(p));

export const REPORT_SUBTABS = Object.freeze([
    Object.freeze({ key: "PDFReport",    label: "PDF Report",   id: "sub-menu-report-pdf" }),
    Object.freeze({
        key: "Expenditures", label: "Expenditures", id: "sub-menu-expenditures",
        requiresAll: EXPENDITURES_PERMISSIONS,
    }),
]);

/** The Reports sub-tabs this user may open, in strip order. */
export function reportSubTabs(can) {
    return REPORT_SUBTABS.filter((t) => !t.requiresAll || t.requiresAll.every((p) => can(p)));
}

/**
 * Whether a sub-menu key is one this user may open.
 *
 * Unknown keys pass, matching canSeeSalesSubTab: the other tabs' sub-menus are
 * not this file's business, and callers see all of them.
 */
export function canSeeReportSubTab(key, can) {
    const tab = REPORT_SUBTABS.find((t) => t.key === key);
    return !tab || !tab.requiresAll || tab.requiresAll.every((p) => can(p));
}

/**
 * Where the Reports tab lands. The PDF generator, which is what the tab has
 * always opened on and needs nothing beyond the reports.view the tab itself
 * requires — falling back to the first view the user can actually read.
 */
export function defaultReportSubMenu(can) {
    return canSeeReportSubTab("PDFReport", can) ? "PDFReport" : (reportSubTabs(can)[0]?.key ?? "");
}
